import { useState, useEffect, useMemo, useRef } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  X,
  FolderTree,
  ToggleLeft,
  ToggleRight,
  Loader2,
  Filter,
  ArrowUp,
  ArrowDown,
  Save,
} from "lucide-react";
import BaseApi from "../../services/base-api";
import { Category } from "../../types/Category";
import { useToast } from "../../context/ToastContext";
import ImageUploader from "../../components/ImageUploader";
import ConfirmModal from "../../components/Modals/Confirm";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import LoadingComponent from "../../components/LoadingComponent";
import { useDebounce } from "../../hooks/useDebounce";
import Pagination from "../../components/Pagination";
import { ImageWithFallback } from "../../constants/image-placeholder";

const CategoriesManagementPage = () => {
  const { success, err } = useToast();
  const { t } = useTranslation();
  const { permissions } = useAuth();

  const canCreateCategory = hasPermission(
    permissions,
    PERMISSIONS.CREATE_CATEGORIES,
  );
  const canEditCategory = hasPermission(
    permissions,
    PERMISSIONS.EDIT_CATEGORIES,
  );
   const canDeleteCategory = hasPermission(
    permissions,
    PERMISSIONS.DELETE_CATEGORIES,
  );
  const canAddCategory = canCreateCategory || canEditCategory;

  // ========== SEARCH STATE (Backend) ==========
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery.trim());

  // ========== FILTER STATE (Client-side) ==========
  const [filters, setFilters] = useState({
    status: "all", // all, active, inactive
    hasProducts: "all", // all, yes, no
  });

  // ========== SORT STATE (Client-side) ==========
  const [sortBy, setSortBy] = useState<"name" | "date" | "products">("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // ========== PAGINATION STATE ==========
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ========== DATA STATE ==========
  const [apiData, setApiData] = useState<Category[]>([]);
  const [apiTotalItems, setApiTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const latestRequestRef = useRef(0);

  // ========== UI STATE ==========
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: t("Delete"),
    cancelText: t("Cancel"),
    onConfirm: () => {},
  });

  const emptyCategoryData = {
    id: "",
    name: { en: "", ar: "" },
    slug: "",
    description: { en: "", ar: "" },
    parentId: null,
    isActive: true,
    sortOrder: 0,
    createdAt: "",
    updatedAt: "",
    deletedAt: "",
    subcategories: [],
    parent: null,
    productCount: 0,
    image: "",
  };

  const [formData, setFormData] = useState<Category>(emptyCategoryData);
  const [errors, setErrors] = useState({
    nameEn: "",
    nameAr: "",
    slug: "",
    image: "",
  });

  // ========== DEBOUNCE SEARCH ==========
  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 on search
  }, [debouncedSearch]);

  // ========== FETCH DATA FROM API (with backend search) ==========
  const fetchData = async () => {
    const requestId = ++latestRequestRef.current;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });

      // ✅ Backend Search
      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }

      const res = await BaseApi.get(`/categories?${params.toString()}`);

      // Ignore responses from requests superseded by a newer one
      if (requestId !== latestRequestRef.current) return;

      if (res && res.data) {
        setApiData(res.data);
        setApiTotalItems(res.pagination?.totalItems || res.data.length);
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error("Failed to fetch categories");
      err(t("Failed to load categories"));
    } finally {
      if (requestId === latestRequestRef.current) {
        setIsLoading(false);
      }
    }
  };

  // Re-fetch when search changes
  useEffect(() => {
    fetchData();
  }, [debouncedSearch, currentPage, itemsPerPage]);

  // ========== CLIENT-SIDE FILTERS ==========
  const filteredData = useMemo(() => {
    let result = [...apiData];

    // Filter by status
    if (filters.status === "active") {
      result = result.filter((item) => item.isActive === true);
    } else if (filters.status === "inactive") {
      result = result.filter((item) => item.isActive === false);
    }

    // Filter by products
    if (filters.hasProducts === "yes") {
      result = result.filter((item) => (item.productCount || 0) > 0);
    } else if (filters.hasProducts === "no") {
      result = result.filter((item) => (item.productCount || 0) === 0);
    }

    return result;
  }, [apiData, filters]);

  // ========== CLIENT-SIDE SORTING ==========
  const sortedData = useMemo(() => {
    const sorted = [...filteredData];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "name":
          comparison = (a.name.en || "").localeCompare(b.name.en || "");
          break;
        case "date":
          comparison =
            new Date(a.createdAt || 0).getTime() -
            new Date(b.createdAt || 0).getTime();
          break;
        case "products":
          comparison = (a.productCount || 0) - (b.productCount || 0);
          break;
        default:
          comparison = 0;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [filteredData, sortBy, sortOrder]);

  // ========== PAGINATION CALCULATION ==========
  const finalTotalItems = sortedData.length;
  const finalTotalPages = Math.ceil(finalTotalItems / itemsPerPage);

  // Ensure current page doesn't exceed total pages
  useEffect(() => {
    if (currentPage > finalTotalPages && finalTotalPages > 0) {
      setCurrentPage(finalTotalPages);
    }
  }, [currentPage, finalTotalPages]);

  // Get current page data
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = sortedData.slice(startIndex, endIndex);

  // ========== HANDLERS ==========
  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to page 1 when filter changes
  };

  const handleSortChange = (field: "name" | "date" | "products") => {
    if (sortBy === field) {
      // Toggle sort order if same field
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      // Set new field with ascending order
      setSortBy(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setFilters({ status: "all", hasProducts: "all" });
    setSortBy("name");
    setSortOrder("asc");
    setCurrentPage(1);
  };

  const hasActiveFilters = () => {
    return (
      searchQuery.trim() !== "" ||
      filters.status !== "all" ||
      filters.hasProducts !== "all"
    );
  };

  // ========== CRUD OPERATIONS ==========
  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith("name.") || field.startsWith("description.")) {
      const [parent, lang] = field.split(".");
      setFormData({
        ...formData,
        [parent]: {
          ...(formData[parent as keyof Category] as any),
          [lang]: value,
        },
      });
    } else {
      setFormData({ ...formData, [field]: value });
    }

    if (field === "name.en" && !isEditing) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      setFormData((prev) => ({ ...prev, slug }));
    }

    if (errors[field as keyof typeof errors]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = { nameEn: "", nameAr: "", slug: "", image: "" };

    if (!(formData.name.en || "").trim())
      newErrors.nameEn = t("English name is required");
    if (!(formData.name.ar || "").trim())
      newErrors.nameAr = t("Arabic name is required");
    if (!formData.slug.trim()) {
      newErrors.slug = "Slug is required";
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = t(
        "Slug can only contain lowercase letters, numbers, and hyphens",
      );
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const payload: any = {
        name: formData.name,
        slug: formData.slug,
        image: formData.image,
        isActive: formData.isActive,
        description: formData.description,
        sortOrder: formData.sortOrder,
      };

      if (formData.parentId) {
        payload.parentId = formData.parentId;
      }

      if (isEditing && formData.id) {
        const res = await BaseApi.put(`/categories/${formData.id}`, payload);
        if (res && res.success) {
          success(t("Category updated successfully!"));
          closeModal();
          await fetchData();
        }
      } else {
        const res = await BaseApi.post("/categories", payload);
        if (res && res.success) {
          success(t("Category created successfully!"));
          closeModal();
          await fetchData();
          
        }
      }
    } catch (error) {
      console.error("Failed to save category", error);
      err(t("Failed to save category"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (category: Category) => {
    setFormData(category);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await BaseApi.delete(`/categories/${id}`);
      if (res && res.success) {
        success(t("Category deleted successfully!"));
        await fetchData();
      }
    } catch (error) {
      console.error("Failed to delete category");
      err(t("Failed to delete category"));
    }
  };

  const openAddModal = () => {
    setFormData(emptyCategoryData);
    setErrors({ nameEn: "", nameAr: "", slug: "", image: "" });
    setIsEditing(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData(emptyCategoryData);
    setErrors({ nameEn: "", nameAr: "", slug: "", image: "" });
    setIsEditing(false);
  };

  const handleDeleteRequest = (id: string, name: string) => {
    setConfirmConfig({
      isOpen: true,
      title: t("Confirm Delete"),
      message: `${t("Are you sure you want to delete this category")} '${name}'?`,
      confirmText: t("Delete"),
      cancelText: t("Cancel"),
      onConfirm: () => {
        handleDelete(id);
        setConfirmConfig((c) => ({ ...c, isOpen: false }));
      },
    });
  };

  // ========== SUMMARY CARDS ==========
  const categorySummary = (
    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-4 sm:mb-6">
      <div className="bg-white rounded-xl shadow-md p-4 flex items-center gap-3">
        <div className="bg-blue-100 text-blue-600 p-3 rounded-full">
          <FolderTree className="w-5 h-5" />
        </div>
        <div>
          <p className="text-md text-blue-500 font-bold">{t("Total")}</p>
          <p className="text-lg font-bold text-blue-900">{finalTotalItems || apiTotalItems}</p>
        </div>
      </div>

      {/* Active Category */}
      <div className="bg-white rounded-xl shadow-md p-4 flex items-center gap-3">
        <div className="bg-green-100 text-green-600 p-3 rounded-full">
          <ToggleRight className="w-5 h-5" />
        </div>
        <div>
          <p className="text-md text-green-500 font-bold">{t("Active")}</p>
          <p className="text-lg font-bold text-green-900">
            {apiData?.filter((c) => c.isActive).length}
          </p>
        </div>
      </div>

      {/* InActive Category */}
      <div className="bg-white rounded-xl shadow-md p-4 flex items-center gap-3">
        <div className="bg-red-100 text-red-600 p-3 rounded-full">
          <ToggleLeft className="w-5 h-5" />
        </div>
        <div>
          <p className="text-md text-red-500 font-bold">{t("Inactive")}</p>
          <p className="text-lg font-bold text-red-900">
            {apiData.filter((c) => !c.isActive).length}
          </p>
        </div>
      </div>
      {hasActiveFilters() && (
        <div className="bg-white rounded-xl shadow-md p-4 flex items-center gap-3">
          <div className="bg-purple-100 text-purple-600 p-3 rounded-full">
            <Filter className="w-5 h-5" />
          </div>
          <div>
            <p className="text-md text-purple-500 font-bold">{t("Filtered")}</p>
            <p className="text-lg font-bold text-gray-900">{finalTotalItems}</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f2e6db] py-8 px-4">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="mx-auto">
        {/* Header */}
        <div
          className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6"
          style={{ animation: "fadeIn 0.5s ease-out" }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
                <FolderTree className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                  {t("Categories")}
                </h1>
                <p className="text-sm sm:text-base text-gray-600">
                  {t("Manage your product categories")}
                </p>
              </div>
            </div>

            <Tooltip
              title={canAddCategory ? t("Create Category") : t("No permission")}
              placement="top"
            >
              <button
                disabled={!canAddCategory}
                onClick={openAddModal}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-black text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-700 transition-all duration-300 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-semibold">{t("Create Category")}</span>
              </button>
            </Tooltip>
          </div>

          {/* Summary */}
          {categorySummary}

          {/* Search & Filters */}
          <div className="mt-6 space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-12 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
                placeholder={t("Search categories by name or slug...")}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Filter Controls */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                  showFilters || hasActiveFilters()
                    ? "border-black bg-black text-white"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <Filter className="w-4 h-4" />
                {t("Filters")}
                {hasActiveFilters() && (
                  <span className="bg-white text-black rounded-full w-5 h-5 text-xs flex items-center justify-center font-bold">
                    !
                  </span>
                )}
              </button>

              {showFilters && (
                <>
                  {/* Status Filter */}
                  <select
                    value={filters.status}
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                    className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none"
                  >
                    <option value="all">{t("All Status")}</option>
                    <option value="active">{t("Active")}</option>
                    <option value="inactive">{t("Inactive")}</option>
                  </select>

                  {/* Products Filter */}
                  <select
                    value={filters.hasProducts}
                    onChange={(e) =>
                      handleFilterChange("hasProducts", e.target.value)
                    }
                    className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none"
                  >
                    <option value="all">{t("All Categories")}</option>
                    <option value="yes">{t("With Products")}</option>
                    <option value="no">{t("Without Products")}</option>
                  </select>
                </>
              )}

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value as any)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-black focus:outline-none"
              >
                <option value="name">{t("Sort by Name")}</option>
                <option value="date">{t("Sort by Date")}</option>
                <option value="products">{t("Sort by Products")}</option>
              </select>

              <button
                onClick={() =>
                  setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
                }
                className="p-2 border-2 border-gray-200 rounded-lg hover:border-gray-300"
                title={sortOrder === "asc" ? t("Ascending") : t("Descending")}
              >
                {sortOrder === "asc" ? (
                  <ArrowUp className="w-5 h-5" />
                ) : (
                  <ArrowDown className="w-5 h-5" />
                )}
              </button>

              {hasActiveFilters() && (
                <button
                  onClick={clearAllFilters}
                  className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                >
                  {t("Clear All")}
                </button>
              )}
            </div>

            {/* Active Filters Display */}
            {hasActiveFilters() && (
              <div className="flex flex-wrap gap-2">
                {debouncedSearch && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    Search: "{debouncedSearch}"
                  </span>
                )}
                {filters.status !== "all" && (
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                    Status: {filters.status}
                  </span>
                )}
                {filters.hasProducts !== "all" && (
                  <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                    Products: {filters.hasProducts}
                  </span>
                )}
              </div>
            )}

            {/* Results Info */}
            <p className="text-sm text-gray-600">
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("Loading...")}
                </span>
              ) : (
                <>
                  {t("Showing")} {currentPageData.length} {t("of")}{" "}
                  {finalTotalItems} {t("results")}
                  {debouncedSearch && ` ${t("for")} "${debouncedSearch}"`}
                  {(filters.status !== "all" ||
                    filters.hasProducts !== "all") &&
                    ` (${t("filtered")})`}
                </>
              )}
            </p>
          </div>
        </div>

        {/* Categories Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {isLoading && currentPageData.length === 0 ? (
            <LoadingComponent title={t("Categories")} />
          ) : currentPageData.length === 0 ? (
            <div className="text-center py-16">
              <FolderTree className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-2">
                {hasActiveFilters()
                  ? t("No results found")
                  : t("No categories yet")}
              </p>
              {hasActiveFilters() ? (
                <button
                  onClick={clearAllFilters}
                  className="text-black hover:underline font-semibold"
                >
                  {t("Clear filters")}
                </button>
              ) : canAddCategory ? (
                <button
                  onClick={openAddModal}
                  className="text-black hover:underline font-semibold"
                >
                  {t("Create your first category")}
                </button>
              ) : null}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase">
                        {t("Image")}
                      </th>
                      <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase">
                        {t("Name")}
                      </th>
                      <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase hidden sm:table-cell">
                        {t("Slug")}
                      </th>
                      <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase hidden md:table-cell">
                        {t("Products")}
                      </th>
                      <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase">
                        {t("Status")}
                      </th>
                      <th className="px-4 sm:px-6 py-3 sm:py-4 text-right text-xs font-semibold text-gray-700 uppercase">
                        {t("Actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentPageData.map((category) => (
                      <tr
                        key={category.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <ImageWithFallback
                            src={category.image}
                            alt={category.name.en}
                            className="w-12 h-12 rounded-md object-cover"
                          />
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <div>
                            <p className="font-semibold text-gray-800">
                              {category.name.en}
                            </p>
                            <p className="text-sm text-gray-600">
                              {category.name.ar}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 hidden sm:table-cell">
                          <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                            {category.slug}
                          </code>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 hidden md:table-cell">
                          <span className="text-sm text-gray-600">
                            {category.productCount || 0} products
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <span
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                              category.isActive
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {category.isActive ? (
                              <>
                                <ToggleRight className="w-4 h-4" />
                                {t("Active")}
                              </>
                            ) : (
                              <>
                                <ToggleLeft className="w-4 h-4" />
                                {t("Inactive")}
                              </>
                            )}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="flex items-center justify-end gap-2">
                            {!!canEditCategory && (
                              <button
                                onClick={() => handleEdit(category)}
                                className="p-2 text-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
                              >
                                <Edit2 className="w-5 h-5" />
                              </button>
                            )}
                            {!!canDeleteCategory && (
                              <button
                                onClick={() =>
                                  category.id &&
                                  handleDeleteRequest(
                                    category.id,
                                    category.name.en || "",
                                  )
                                }
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ✅ UNIFIED PAGINATION - Works with everything! */}
              {finalTotalItems > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={finalTotalPages}
                  totalItems={finalTotalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(size) => {
                    setItemsPerPage(size);
                    setCurrentPage(1);
                  }}
                  pageSizeOptions={[5, 10, 25, 50, 100]}
                  showPageSizeSelector={true}
                  isLoading={isLoading}
                  compact={true}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <>
          <div
            className="flex flex-col fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "fadeIn 0.3s ease-out" }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              style={{ animation: "slideDown 0.3s ease-out" }}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-black via-gray-700 to-black px-4 sm:px-6 py-3 sm:py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-1000">
                <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                  <FolderTree className="w-5 h-5 sm:w-6 sm:h-6" />
                  {isEditing ? "Edit Category" : "Add New Category"}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-white hover:text-gray-300 transition-colors p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                {/* English Name */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    {t("English Name *")}
                  </label>
                  <input
                    type="text"
                    value={formData.name.en}
                    onChange={(e) =>
                      handleInputChange("name.en", e.target.value)
                    }
                    className={`w-full px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${
                      errors.nameEn
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-200 focus:border-black"
                    } focus:outline-none transition-all duration-300`}
                    placeholder="Enter category name in English"
                  />
                  {errors.nameEn && (
                    <p className="mt-2 text-xs sm:text-sm text-red-500">
                      {errors.nameEn}
                    </p>
                  )}
                </div>

                {/* Arabic Name */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    {t("Arabic Name *")}
                  </label>
                  <input
                    type="text"
                    value={formData.name.ar}
                    onChange={(e) =>
                      handleInputChange("name.ar", e.target.value)
                    }
                    className={`w-full px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${
                      errors.nameAr
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-200 focus:border-black"
                    } focus:outline-none transition-all duration-300`}
                    placeholder="أدخل اسم الفئة بالعربية"
                    dir="rtl"
                  />
                  {errors.nameAr && (
                    <p className="mt-2 text-xs sm:text-sm text-red-500">
                      {errors.nameAr}
                    </p>
                  )}
                </div>

                {/* Slug */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    {t("Slug *")}
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => handleInputChange("slug", e.target.value)}
                    className={`w-full px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 ${
                      errors.slug
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-200 focus:border-black"
                    } focus:outline-none transition-all duration-300`}
                    placeholder="category-slug"
                  />
                  {errors.slug && (
                    <p className="mt-2 text-xs sm:text-sm text-red-500">
                      {errors.slug}
                    </p>
                  )}
                </div>

                {/* Parent Category */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    {t("Parent Category (Optional)")}
                  </label>
                  <select
                    value={formData.parentId || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "parentId",
                        e.target.value ? e.target.value : null,
                      )
                    }
                    className="w-full px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all duration-300"
                  >
                    <option value="">{t("None (Root Category)")}</option>
                    {apiData
                      .filter((cat) => cat.id !== formData.id)
                      .map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name.en}
                          {category.name.ar && ` (${category.name.ar})`}
                        </option>
                      ))}
                  </select>
                </div>

                <ImageUploader
                  value={formData.image}
                  onChange={(value: string | string[]) => {
                    const image = Array.isArray(value) ? value[0] : value;
                    handleInputChange("image", image);
                  }}
                  error={errors.image}
                  label="Category Image"
                  required
                />

                {/* English Description */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    {t("English Description (Optional)")}
                  </label>
                  <textarea
                    value={formData.description.en || ""}
                    onChange={(e) =>
                      handleInputChange("description.en", e.target.value)
                    }
                    rows={3}
                    className="w-full px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all duration-300"
                    placeholder="Enter category description in English"
                  />
                </div>

                {/* Arabic Description */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    {t("Arabic Description (Optional)")}
                  </label>
                  <textarea
                    value={formData.description.ar || ""}
                    onChange={(e) =>
                      handleInputChange("description.ar", e.target.value)
                    }
                    rows={3}
                    className="w-full px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all duration-300"
                    placeholder="أدخل وصف الفئة بالعربية"
                    dir="rtl"
                  />
                </div>

                {/* Sort Order */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    {t("Sort Order")}
                  </label>
                  <input
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) =>
                      handleInputChange(
                        "sortOrder",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    className="w-full px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all duration-300"
                    placeholder="0"
                    min="0"
                  />
                </div>

                {/* Active Status */}
                <div className="pt-4 border-t-2 border-gray-100">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) =>
                        handleInputChange("isActive", e.target.checked)
                      }
                      className="w-5 h-5 rounded border-gray-300 text-black cursor-pointer"
                    />
                    <div>
                      <span className="text-sm font-semibold text-gray-700">
                        {t("Active Category")}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {t("This category will be visible on the storefront")}
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-4 sm:px-6 pb-4 sm:pb-6 flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={closeModal}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-300 transition-all duration-300 font-semibold text-sm sm:text-base"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex-1 bg-black text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-700 transition-all duration-300 font-semibold disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  {isSaving ? (
                    <>
                      <div
                        className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full"
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                      <span>{t("Saving...")}</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 sm:w-5 sm:h-5" />
                      {isEditing ? t("Update Category") : t("Create Category")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        icon={Trash2}
        iconColor="text-red-600"
        iconBg="bg-red-100"
        onConfirm={confirmConfig.onConfirm}
        onCancel={() => setConfirmConfig((c) => ({ ...c, isOpen: false }))}
      />
    </div>
  );
};

export default CategoriesManagementPage;
